import { Component, OnDestroy, OnInit } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import DateHelper from 'src/app/helpers/date-helper';
import { Tour } from 'src/app/model/tour/tour';
import { ImmoIPTourOrderChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-tour-order-changes/immo-ip-tour-order-changes.service';
import { ImmoIPTourRouteCheckedChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-tour-route-checked-changes/immo-ip-tour-route-checked-changes.service';
import { ImmoIPPlacerTourRouteCheckedSubmitService } from 'src/app/services/immo-ip-placer-tour-route-checked-submit/immo-ip-placer-tour-route-checked-submit.service';
import { PlanningDateService } from 'src/app/services/planning-date-service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { EMPTY_OBJECT } from 'src/globals/globals';
import { ImmoIPPlacerTourOrderHelper } from './helper/immo-ip-placer-tour-order-helper';
import {
  ImmoIPPlacerTourViewHelper,
  SELECTABLE_VIEWS,
} from './helper/immo-ip-placer-tour-view-helper';
import { OfflineStorageTourService } from './offline-storage/offline-storage-tour.service';

@Component({
  selector: 'immo-ip-placer-tour-view',
  templateUrl: './immo-ip-placer-tour-view.component.html',
  styleUrls: ['./immo-ip-placer-tour-view.component.scss'],
})
export class ImmoIPPlacerTourViewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  tour: Tour;
  selectedView: SELECTABLE_VIEWS;
  SELECTABLE_VIEWS = SELECTABLE_VIEWS;

  private date: Date;

  private getTourSubject = new Subject<any>();
  tourDateChangedOrDestroy = new Subject<any>();

  storingTourRouteChecked = false;

  EMPTY_OBJECT = EMPTY_OBJECT;

  constructor(
    private planningDateService: PlanningDateService,
    private offlineStorageTourService: OfflineStorageTourService,
    private immoIPTourOrderChangesService: ImmoIPTourOrderChangesService,
    private immoIPTourRouteCheckedChangesService: ImmoIPTourRouteCheckedChangesService,
    private immoIPPlacerTourRouteCheckedSubmitService: ImmoIPPlacerTourRouteCheckedSubmitService
  ) {
    super();
  }

  ngOnInit(): void {
    this.selectedView = ImmoIPPlacerTourViewHelper.getStoredSelectedView();
    this.addSubscription(
      this.getTourSubject.pipe(
        startWith(EMPTY_OBJECT),
        switchMap(() => {
          this.tour = EMPTY_OBJECT;
          if (this.date == null) {
            return of(EMPTY_OBJECT);
          }
          return this.offlineStorageTourService.getTour(
            DateHelper.formatDate(this.date, 'yyyyMMdd')
          );
        }),
        catchError(() => {
          return of(EMPTY_OBJECT);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      async (res: Tour) => {
        await ImmoIPPlacerTourOrderHelper.applyLocalTourOrderChanges(
          res,
          this.immoIPTourOrderChangesService
        );
        await ImmoIPPlacerTourViewHelper.applyLocalTourRouteCheckedChanges(
          res,
          this.immoIPTourRouteCheckedChangesService
        );
        this.tour = res;
        if (this.tour != EMPTY_OBJECT && !this.tour.routeChecked)
          this.setSelectedView(SELECTABLE_VIEWS.IMMO_IP_PLACER_TOUR_ROUTE_VIEW);
      }
    );
    this.addSubscription(
      this.planningDateService.getSelectedPlanningDateSubscribable(),
      (date: Date) => {
        this.date = date;
        this.getTourSubject.next();
        this.tourDateChangedOrDestroy.next();
      }
    );
    // TODO submit queue logic
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getTourSubject != null) {
      this.getTourSubject.complete();
    }
    if (this.tourDateChangedOrDestroy != null) {
      this.tourDateChangedOrDestroy.next();
      this.tourDateChangedOrDestroy.complete();
    }
  }

  setSelectedView(selectedView: SELECTABLE_VIEWS): void {
    this.selectedView = selectedView;
    ImmoIPPlacerTourViewHelper.setStoredSelectedView(selectedView);
  }

  submitTourRouteChecked(): void {
    this.storingTourRouteChecked = true;
    ImmoIPPlacerTourViewHelper.submitTourRouteChecked(
      this.tour,
      DateHelper.formatDate(this.date, 'yyyyMMdd'),
      this.immoIPTourRouteCheckedChangesService,
      this.immoIPPlacerTourRouteCheckedSubmitService
    ).finally(() => (this.storingTourRouteChecked = false));
  }
}
